import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import InputSelect from '../common/InputSelect';
import AdminInput from '../common/AdminInput';
import Button from '../common/Button';
import { moderatorSchema } from '../../schema/moderator.schema';
import { getTranslation } from '../../utils/translation';
import Spinner from '../common/Spinner';

const initialValues = {
  title: '',
  firstName: '',
  lastName: '',
  email: ''
};

const titleOptions = [
  { value: '', label: 'placeholder' },
  { value: 'mr', label: 'mr' },
  { value: 'mrs', label: 'mrs' },
  { value: 'diverse', label: 'diverse' }
];

const ModeratorForm = ({ onSubmit, onDelete, storedData = {} }) => {
  const { t } = useTranslation();
  const formik = useFormik({
    initialValues,
    validationSchema: moderatorSchema,
    onSubmit: async (values) => {
      setLoading(true);
      await onSubmit(values);
      setLoading(false);
    },
    enableReinitialize: true
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (storedData && Object.keys(storedData).length > 0) {
      formik.setValues({
        ...initialValues,
        ...storedData
      });
    }
  }, [storedData]);

  return (
    <form onSubmit={formik.handleSubmit} className="mt-5 block w-full mx-auto px-5 relative">
      <div className="mb-6">
        <label className="block mb-1 text-base font-bold text-cblack-primary">
          {getTranslation(t, 'moderatorForm.fields.title')}
        </label>
        <InputSelect
          name="title"
          value={formik.values.title}
          options={titleOptions.map((option) => ({
            ...option,
            label: getTranslation(t, `moderatorForm.options.${option.label}`)
          }))}
          onChange={(value) => formik.setFieldValue('title', value)}
          error={formik.errors.title}
          touched={formik.touched.title}
        />
      </div>
      <div className="mb-6">
        <AdminInput
          name="firstName"
          label={getTranslation(t, 'moderatorForm.fields.firstName')}
          placeholder={getTranslation(t, 'moderatorForm.placeholder.firstName')}
          formik={formik}
        />
      </div>
      <div className="mb-6">
        <AdminInput
          name="lastName"
          label={getTranslation(t, 'moderatorForm.fields.lastName')}
          placeholder={getTranslation(t, 'moderatorForm.placeholder.lastName')}
          formik={formik}
        />
      </div>
      <div className="mb-6">
        <AdminInput
          name="email"
          label={getTranslation(t, 'moderatorForm.fields.email')}
          placeholder={getTranslation(t, 'moderatorForm.placeholder.email')}
          formik={formik}
          disabled={storedData?.email}
        />
      </div>
      <div className="mt-6 mb-6 flex gap-2 flex-wrap items-center">
        {onDelete && (
          <Button
            type="button"
            buttonType="outline"
            onClick={async () => {
              setLoading(true);
              await onDelete(storedData._id);
              setLoading(false);
            }}>
            {getTranslation(t, 'moderatorForm.deleteButton')}
          </Button>
        )}
        <Button
          type="submit"
          buttonType="primary"
          loading={formik.isSubmitting}
          disabled={formik.isSubmitting || Object.keys(formik.errors)?.length > 0}>
          {Object.keys(storedData)?.length > 0
            ? getTranslation(t, 'moderatorForm.updateButton')
            : getTranslation(t, 'moderatorForm.saveButton')}
        </Button>
      </div>

      {loading && (
        <div className="absolute w-full h-full top-0 left-0 flex items-center justify-center bg-white/50">
          <Spinner size={50} />
        </div>
      )}
    </form>
  );
};

export default ModeratorForm;
